import React, { useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { getConfig } from '../lib/configMgr';
import '../styles/SourceCard.css';

const { ExternalRESTAPI } = getConfig();

const GENERIC_TITLES = new Set([
  'unichem', 'not found', 'page not found', '404', '403', 'forbidden',
  'error', 'access denied', 'service unavailable', 'bad gateway',
]);

// Cache favicon URLs by domain to avoid repeated failed lookups
const faviconCache = new Map();

function isUnhelpfulTitle(metaTitle, url) {
  const lower = metaTitle.trim().toLowerCase();
  if (GENERIC_TITLES.has(lower)) return true;
  try {
    const domain = new URL(url).hostname.replace(/^www\./, '');
    const domainBase = domain.split('.')[0];
    if (lower === domainBase) return true;
    if (lower.startsWith(domainBase + ' -') || lower.endsWith('- ' + domainBase)) return true;
  } catch { /* ignore */ }
  return false;
}

function dedupeDescription(description, title) {
  if (!description || !title) return description;
  const normDesc = description.toLowerCase().replace(/[^a-z0-9]/g, '');
  const normTitle = title.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (normTitle.startsWith(normDesc)) return null;
  if (normDesc.startsWith(normTitle)) {
    const remainder = stripPrefix(description, title);
    return remainder || null;
  }
  // Description contains title with small prefix (e.g. "Report card for <title>...")
  const idx = normDesc.indexOf(normTitle);
  if (idx >= 0 && normTitle.length / normDesc.length > 0.6) {
    return null;
  }
  return description;
}

function stripPrefix(description, title) {
  let di = 0, ti = 0;
  const descLower = description.toLowerCase();
  const titleLower = title.toLowerCase();
  while (ti < titleLower.length && di < descLower.length) {
    if (/[^a-z0-9]/.test(descLower[di])) { di++; continue; }
    if (/[^a-z0-9]/.test(titleLower[ti])) { ti++; continue; }
    if (descLower[di] === titleLower[ti]) { di++; ti++; }
    else break;
  }
  if (ti >= titleLower.length) {
    return description.slice(di).replace(/^[\s\-–—:,.|]+/, '').trim();
  }
  return description;
}

const SourceCard = inject('sourceReferencesStore', 'searchSessionStore')(observer(({ source, sourceReferencesStore, searchSessionStore }) => {
  const isHighlighted = sourceReferencesStore.isSourceHighlighted(source.id);
  // PLEX-5920: the title/description the app already has for this URL (a resolved Plex entity, or an
  // evidence edge's source_metadata). With a description, the page's own metadata is neither
  // fetched nor used (if a fetch landed first): it is generic for some sites (Open Targets) and
  // blocked for others (PubMed).
  const local = sourceReferencesStore.urlDescriptionMap.get(source.url);
  const hasLocalDescription = Boolean(local?.description);
  const meta = hasLocalDescription ? null : sourceReferencesStore.metadataMap.get(source.id);

  useEffect(() => {
    if (!source.isInvalid && !hasLocalDescription && ExternalRESTAPI.UrlMetadata) {
      sourceReferencesStore.fetchMetadata(source.id, source.url, ExternalRESTAPI.UrlMetadata, searchSessionStore?.authData);
    }
  }, [source.id, source.url, source.isInvalid, hasLocalDescription, sourceReferencesStore, searchSessionStore]);

  const metaTitle = meta && !meta.loading && !meta.error && meta.title;
  const isGenericSiteTitle = metaTitle && isUnhelpfulTitle(metaTitle, source.url);
  // The citation's own link text stays the title: it carries per-citation wording such as "(via
  // similarity to …)". Only when it is just the edge's title and description, as the backend combined
  // them, are the two shown apart; the known title is also the fallback for a link with no text.
  const normalized = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const linkIsCombinedEdgeText = Boolean(local?.title)
    && normalized(source.title) === normalized(`${local.title}${local.description || ''}`);
  const title = linkIsCombinedEdgeText ? local.title
    : (metaTitle && !isGenericSiteTitle) ? metaTitle : (source.title || local?.title || metaTitle);
  const domain = (meta && meta.domain) || (() => {
    try { return new URL(source.url).hostname; } catch { return null; }
  })();
  const rawDescription = local?.description || (meta && !meta.loading ? meta.description : null);
  const description = dedupeDescription(rawDescription, title);
  const faviconUrl = (meta && !meta.loading ? meta.favicon_url : null)
    || (domain && `https://www.google.com/s2/favicons?domain=${domain}&sz=16`);
  // Only treat the card as loading metadata when the store has explicitly
  // marked it loading. The previous default (`!source.isInvalid`) caused the
  // shimmer to stick on permanently when fetchMetadata never runs (e.g. the
  // UrlMetadata endpoint isn't configured).
  const isLoadingMeta = meta?.loading === true;

  const handleFaviconError = (e) => {
    // Cache the failed favicon URL so we don't retry it
    if (faviconUrl) {
      faviconCache.set(faviconUrl, false);
    }
    e.target.style.display = 'none';
  };

  // Truncate URL if too long
  const displayUrl = source.url.length > 60
    ? source.url.substring(0, 57) + '...'
    : source.url;

  const handleMouseEnter = () => sourceReferencesStore.highlightSource([source.id]);
  const handleMouseLeave = () => sourceReferencesStore.clearHighlight();
  const handleClick = () => sourceReferencesStore.scrollToSource(source.id);

  return (
    <div
      className={`source-card ${isHighlighted ? 'highlighted' : ''}`}
      data-source-id={source.id}
      role="article"
      aria-label={`Source: ${title}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      <div className="source-card-title">
        {faviconUrl && faviconCache.get(faviconUrl) !== false && (
          <img
            className="source-card-favicon"
            src={faviconUrl}
            alt=""
            width={16}
            height={16}
            onError={handleFaviconError}
          />
        )}
        {title}
        {source.isInvalid && (
          <span className="invalid-indicator" title="Invalid URL format">⚠️</span>
        )}
      </div>
      {domain && (
        <div className="source-card-domain">{domain}</div>
      )}
      <div className={`source-card-description ${isLoadingMeta && !description ? 'shimmer' : ''}`}>
        {description || null}
      </div>
      <div className="source-card-url">
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
        >
          {displayUrl}
        </a>
      </div>
      {source.occurrences.length > 1 && (
        <div className="source-card-occurrences">
          {source.occurrences.length} references
        </div>
      )}
    </div>
  );
}));

export default SourceCard;